import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import type { Controls, EventData, TooltipRenderProps } from 'react-joyride';
import { getPostAwardActionTourSteps } from '../../stages/PostAwardAction.tour';
import { TrackedJoyride } from './TrackedJoyride';
import TourFooter from './TourFooter';
import { TourHintContext } from './TourHintContext';
import { getTourJoyride } from './tourJoyrideStyles';
import { useTourSeen } from './useTourSeen';
import type { WalkthroughTourKey } from '../../api/activityFunctions';

export interface PostAwardActionTourHandle { replay(): void; }

const stageTourKeys: Record<number, WalkthroughTourKey> = {
  0: 'post-award-intake',
  1: 'post-award-recommendation',
  2: 'post-award-workspace',
};

/** A replayable, keyboard-accessible tour for one visible post-award stage. */
const PostAwardActionTour = forwardRef<PostAwardActionTourHandle, { stageIndex: number }>(
  ({ stageIndex }, ref) => {
    const normalizedStage = Math.min(2, Math.max(0, stageIndex));
    const tourKey = stageTourKeys[normalizedStage];
    const theme = useTheme();
    const { lastIndex, markPhaseAComplete, markSeen, reset } = useTourSeen(tourKey);
    const [run, setRun] = useState(false);
    const [showResumeHint, setShowResumeHint] = useState(false);
    const [startIndex, setStartIndex] = useState(0);
    const [replayNonce, setReplayNonce] = useState(0);
    const currentIndexRef = useRef(0);
    const controlsRef = useRef<Controls | null>(null);
    const showResumeHintRef = useRef(showResumeHint);
    useEffect(() => { showResumeHintRef.current = showResumeHint; }, [showResumeHint]);

    const steps = useMemo(() => getPostAwardActionTourSteps(normalizedStage), [normalizedStage]);
    const handleCallback = useCallback((data: EventData, controls?: Controls) => {
      if (controls) controlsRef.current = controls;
      const { status, action, type, index } = data;
      if (type === EVENTS.STEP_BEFORE && typeof index === 'number') currentIndexRef.current = index;
      if (status === STATUS.SKIPPED || (action === ACTIONS.CLOSE && type === EVENTS.TOUR_END)) {
        markPhaseAComplete(typeof index === 'number' ? index : currentIndexRef.current);
        setShowResumeHint(true);
      } else if (status === STATUS.FINISHED && type === EVENTS.TOUR_END) {
        markSeen();
        setRun(false);
      }
    }, [markPhaseAComplete, markSeen]);

    useEffect(() => {
      if (!run || typeof window === 'undefined') return;
      const onKeyDown = (event: KeyboardEvent) => {
        if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Escape'].includes(event.key)) return;
        event.preventDefault();
        event.stopPropagation();
        if (event.key === 'Escape') {
          if (showResumeHintRef.current) { setShowResumeHint(false); setRun(false); }
          else { markPhaseAComplete(currentIndexRef.current); setShowResumeHint(true); }
          return;
        }
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') controlsRef.current?.next?.();
        else controlsRef.current?.prev?.();
      };
      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
    }, [markPhaseAComplete, run]);

    useImperativeHandle(ref, () => ({
      replay: () => {
        setShowResumeHint(false);
        setReplayNonce(value => value + 1);
        if (lastIndex !== null && lastIndex >= 0) setStartIndex(lastIndex);
        else { reset(); setStartIndex(0); }
        setRun(true);
      },
    }), [lastIndex, reset]);

    const dismissResumeHint = useCallback(() => { setShowResumeHint(false); setRun(false); }, []);
    const requestExit = useCallback(() => { markPhaseAComplete(currentIndexRef.current); setShowResumeHint(true); }, [markPhaseAComplete]);
    const hintContextValue = useMemo(() => ({ isHintMode: showResumeHint, onRequestExit: requestExit, onDismissHint: dismissResumeHint }), [dismissResumeHint, requestExit, showResumeHint]);
    // The global workflow navbar is deliberately layered at z-index 2000.
    // Keep this tour above it so guidance for top-of-stage targets remains
    // readable even when the bubble overlaps the navbar.
    const joyride = useMemo(
      () => getTourJoyride(theme, { zIndex: 2300, hideSpotlight: showResumeHint }),
      [showResumeHint, theme],
    );

    return <TourHintContext.Provider value={hintContextValue}>
      <TrackedJoyride
        tourKey={tourKey}
        key={`joyride-${tourKey}-${startIndex}-${replayNonce}`}
        steps={steps}
        run={run}
        initialStepIndex={startIndex}
        continuous
        locale={{ last: 'Finish' }}
        tooltipComponent={TourFooter as unknown as React.ComponentType<TooltipRenderProps>}
        onEvent={handleCallback}
        options={joyride.options}
        styles={joyride.styles}
      />
    </TourHintContext.Provider>;
  },
);

PostAwardActionTour.displayName = 'PostAwardActionTour';
export default PostAwardActionTour;
